import { isSensitiveKey } from '@aibi/contracts';

/** Categorias de contenido que nunca se aceptan en prompts ni en respuestas de IA. */
export const UNSAFE_CONTENT_KINDS = ['sql', 'code', 'url', 'secret'] as const;
export type UnsafeContentKind = (typeof UNSAFE_CONTENT_KINDS)[number];

export interface GuardFinding {
  readonly kind: UnsafeContentKind;
  /** Ruta del valor dentro del documento; nunca se repite el contenido detectado. */
  readonly path: string;
}

const SQL_PATTERNS: readonly RegExp[] = [
  /\bselect\b[\s\S]{1,200}?\bfrom\b/i,
  /\binsert\s+into\b/i,
  /\bupdate\s+[\w."[\]]+\s+set\b/i,
  /\bdelete\s+from\b/i,
  /\b(?:drop|alter|truncate)\s+(?:table|database|schema|view|index)\b/i,
  /\bcreate\s+(?:or\s+replace\s+)?(?:table|database|schema|view|index|function|procedure)\b/i,
  /\bunion\s+(?:all\s+)?select\b/i,
  /\b(?:grant|revoke)\s+\w+\s+on\b/i,
  /\bmerge\s+into\b/i,
  /\bexec(?:ute)?\s*\(/i,
  /;\s*--/,
  /\/\*[\s\S]*?\*\//,
];

const CODE_PATTERNS: readonly RegExp[] = [
  /<\s*\/?\s*(?:script|iframe|object|embed|style|svg|img|link|meta)\b/i,
  /\bjavascript\s*:/i,
  /\beval\s*\(/i,
  /\bnew\s+Function\b/,
  /\bfunction\s*[\w$]*\s*\([^)]*\)\s*\{/,
  /=>/,
  /\b(?:import|require)\s*\(/,
  /\b(?:process|globalThis|window|document)\s*\.\s*\w+/,
  /\b(?:fetch|setTimeout|setInterval)\s*\(/,
  /\$\{[^}]*\}/,
  /`/,
  /\bon[a-z]+\s*=\s*["']/i,
];

const URL_PATTERNS: readonly RegExp[] = [
  /\b[a-z][a-z0-9+.-]*:\/\//i,
  /\bwww\.[a-z0-9-]+\.[a-z]{2,}/i,
  /\bdata:[a-z]+\/[a-z0-9.+-]+[;,]/i,
  /\b(?:mailto|file|blob):/i,
];

const SECRET_PATTERNS: readonly RegExp[] = [
  /\b(?:password|passwd|pwd|contrase(?:n|ñ)a|clave\s+de\s+acceso|secret|secreto|api[\s_-]?key|access[\s_-]?key|private[\s_-]?key|client[\s_-]?secret|bearer|token|credencial(?:es)?|credentials?)\b/i,
  /\b(?:connection|conexion|conexión)[\s_-]?string\b/i,
  /\bcadena\s+de\s+conexi(?:o|ó)n\b/i,
  /\bsecretRefs?\b/i,
  /\b\w+\s*=\s*[^;\s]+\s*;\s*\w+\s*=/,
  /\bsk-[A-Za-z0-9_-]{16,}/,
  /\bAKIA[0-9A-Z]{16}\b/,
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/,
  /\b(?:ghp|gho|xox[abp])[-_][A-Za-z0-9-]{10,}/,
];

const DETECTORS: readonly (readonly [UnsafeContentKind, readonly RegExp[]])[] = [
  ['sql', SQL_PATTERNS],
  ['code', CODE_PATTERNS],
  ['url', URL_PATTERNS],
  ['secret', SECRET_PATTERNS],
];

/**
 * Categorias de contenido inseguro presentes en un texto. La deteccion es conservadora:
 * un falso positivo solo rechaza la peticion, un falso negativo podria filtrar un secreto
 * o colar SQL. El texto nunca se interpreta ni se ejecuta.
 *
 * @example scanText('DROP TABLE ventas') // ['sql']
 */
export function scanText(text: string): UnsafeContentKind[] {
  const kinds: UnsafeContentKind[] = [];
  for (const [kind, patterns] of DETECTORS) {
    if (patterns.some((pattern) => pattern.test(text))) kinds.push(kind);
  }
  return kinds;
}

const MAX_SCAN_DEPTH = 64;

/**
 * Recorre un valor JSON y devuelve los hallazgos por ruta: textos inseguros y claves
 * que nombran secretos. Los objetos demasiado anidados se tratan como codigo.
 */
export function scanValue(value: unknown, path = ''): GuardFinding[] {
  const findings: GuardFinding[] = [];
  const visit = (node: unknown, at: string, depth: number): void => {
    if (depth > MAX_SCAN_DEPTH) {
      findings.push({ kind: 'code', path: at });
      return;
    }
    if (typeof node === 'string') {
      for (const kind of scanText(node)) findings.push({ kind, path: at });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item, i) => {
        visit(item, join(at, String(i)), depth + 1);
      });
      return;
    }
    if (node !== null && typeof node === 'object') {
      for (const [key, child] of Object.entries(node)) {
        const childPath = join(at, key);
        if (isSensitiveKey(key)) findings.push({ kind: 'secret', path: childPath });
        else for (const kind of scanText(key)) findings.push({ kind, path: childPath });
        visit(child, childPath, depth + 1);
      }
    }
  };
  visit(value, path, 0);
  return findings;
}

const join = (base: string, segment: string): string =>
  base === '' ? segment : `${base}.${segment}`;

/** Descripcion neutra de las categorias detectadas (sin repetir el contenido). */
export function describeFindings(kinds: readonly UnsafeContentKind[]): string {
  const labels: Record<UnsafeContentKind, string> = {
    sql: 'SQL',
    code: 'codigo',
    url: 'URLs',
    secret: 'posibles secretos o credenciales',
  };
  return [...new Set(kinds)].map((kind) => labels[kind]).join(', ');
}
